import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar, Clock, Users, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

const ReservationDialog = ({ barId, barName, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [formData, setFormData] = useState({
    date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    time: '',
    party_size: 2,
    special_requests: ''
  });

  useEffect(() => {
    if (isOpen && formData.date) {
      fetchAvailability();
    }
  }, [isOpen, formData.date]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/bars/${barId}/availability?date=${formData.date}`);
      setAvailability(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.time) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/reservations`, {
        bar_id: barId,
        date: formData.date,
        time: formData.time,
        party_size: formData.party_size,
        special_requests: formData.special_requests || null
      });
      
      toast.success('Reservation created! You earned 5 points.');
      setIsOpen(false);
      setFormData({
        date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        time: '',
        party_size: 2,
        special_requests: ''
      });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  // Generate next 7 days for date selection
  const dateOptions = [];
  for (let i = 1; i <= 7; i++) {
    const date = addDays(new Date(), i);
    dateOptions.push({
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'EEE, MMM d')
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="glass border border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            Reserve a Table at {barName}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date Selection */}
          <div>
            <label className="text-sm text-slate-300 mb-2 block">Select Date</label>
            <div className="grid grid-cols-4 gap-2">
              {dateOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, date: option.value, time: '' })}
                  className={`p-2 rounded-lg text-xs text-center transition-all ${
                    formData.date === option.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <label className="text-sm text-slate-300 mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Select Time
            </label>
            {availability ? (
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                {availability.slots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    disabled={!slot.available}
                    onClick={() => setFormData({ ...formData, time: slot.time })}
                    className={`p-2 rounded-lg text-sm transition-all ${
                      !slot.available
                        ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
                        : formData.time === slot.time
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {slot.time}
                    {formData.time === slot.time && <Check className="w-3 h-3 inline ml-1" />}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            )}
          </div>

          {/* Party Size */}
          <div>
            <label className="text-sm text-slate-300 mb-2 block flex items-center gap-2">
              <Users className="w-4 h-4" />
              Party Size
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6].map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setFormData({ ...formData, party_size: size })}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                    formData.party_size === size
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {size}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, party_size: 7 })}
                className={`px-3 h-10 rounded-lg text-sm font-medium transition-all ${
                  formData.party_size > 6
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                7+
              </button>
            </div>
            {formData.party_size > 6 && (
              <Input
                type="number"
                min="7"
                value={formData.party_size}
                onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) || 7 })}
                className="mt-2 bg-slate-800 border-slate-700 text-white w-24"
              />
            )}
          </div>

          {/* Special Requests */}
          <div>
            <label className="text-sm text-slate-300 mb-2 block">Special Requests (optional)</label>
            <Textarea
              value={formData.special_requests}
              onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
              placeholder="Birthday celebration, dietary restrictions, etc."
              className="bg-slate-800 border-slate-700 text-white"
              rows={2}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={loading || !formData.time}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reserving...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Confirm Reservation
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDialog;
